import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { eventsAPI, SITE_NAMES } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Pencil, Trash2, MoreVertical, Calendar, MapPin } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

export const EventsListAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsAPI.getAll(false);
      setEvents(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst události');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await eventsAPI.delete(deleteId);
      setEvents(events.filter(e => e.id !== deleteId));
      toast.success('Událost byla smazána');
    } catch (error) {
      toast.error('Nepodařilo se smazat událost');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Události</h1>
          <p className="text-[#64748B]">Správa kalendáře akcí</p>
        </div>
        <Link to="/admin/udalosti/nova">
          <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-event">
            <Plus size={18} className="mr-2" />
            Nová událost
          </Button>
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Název</th>
              <th className="hidden md:table-cell">Datum</th>
              <th className="hidden md:table-cell">Místo</th>
              <th>Stav</th>
              <th className="w-20">Akce</th>
            </tr>
          </thead>
          <tbody>
            {events.length > 0 ? events.map((event) => (
              <tr key={event.id} data-testid={`event-row-${event.id}`}>
                <td>
                  <div className="font-medium text-[#2C3E50]">{event.title}</div>
                  <div className="text-xs text-[#64748B] md:hidden">{formatDate(event.start_date)}</div>
                </td>
                <td className="hidden md:table-cell text-[#64748B]">{formatDate(event.start_date)}</td>
                <td className="hidden md:table-cell text-[#64748B]">{event.location || '-'}</td>
                <td>
                  <span className={`badge ${event.is_published ? 'badge-success' : 'badge-warning'}`}>
                    {event.is_published ? 'Publikováno' : 'Koncept'}
                  </span>
                </td>
                <td>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/admin/udalosti/${event.id}`} className="cursor-pointer">
                          <Pencil size={14} className="mr-2" />Upravit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(event.id)} className="cursor-pointer text-red-600">
                        <Trash2 size={14} className="mr-2" />Smazat
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">Žádné události</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat událost?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const EventFormAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    is_published: true,
    site: 'ms'
  });

  useEffect(() => {
    if (isEdit) loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      const response = await eventsAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst událost');
      navigate('/admin/udalosti');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await eventsAPI.update(id, formData);
        toast.success('Událost byla aktualizována');
      } else {
        await eventsAPI.create(formData);
        toast.success('Událost byla vytvořena');
      }
      navigate('/admin/udalosti');
    } catch (error) {
      toast.error('Nepodařilo se uložit událost');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/udalosti" className="text-[#64748B] hover:text-[#4ECDC4]"><ArrowLeft size={24} /></Link>
        <h1 className="text-2xl font-bold text-[#2C3E50]">{isEdit ? 'Upravit událost' : 'Nová událost'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Název *</Label>
          <Input id="title" name="title" value={formData.title} onChange={handleChange} required className="rounded-xl" data-testid="event-title" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Popis</Label>
          <Textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} rows={4} className="rounded-xl resize-none" data-testid="event-description" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Datum začátku *</Label>
            <Input id="start_date" name="start_date" type="date" value={formData.start_date} onChange={handleChange} required className="rounded-xl" data-testid="event-start-date" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_date">Datum konce</Label>
            <Input id="end_date" name="end_date" type="date" value={formData.end_date || ''} onChange={handleChange} className="rounded-xl" data-testid="event-end-date" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">Místo konání</Label>
          <Input id="location" name="location" value={formData.location || ''} onChange={handleChange} className="rounded-xl" placeholder="např. Tělocvična MŠ" data-testid="event-location" />
        </div>

        <div className="space-y-2">
          <Label>Sekce</Label>
          <Select value={formData.site || 'ms'} onValueChange={(value) => setFormData(prev => ({ ...prev, site: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="event-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
              <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <div className="font-medium text-[#2C3E50]">Publikovat</div>
            <div className="text-sm text-[#64748B]">Zobrazit v kalendári</div>
          </div>
          <Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))} data-testid="event-publish-switch" />
        </div>

        <div className="flex gap-4 pt-4">
          <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="event-save">
            {saving ? 'Ukládání...' : (isEdit ? 'Uložit změny' : 'Vytvořit událost')}
          </Button>
          <Link to="/admin/udalosti" className="flex-1">
            <Button type="button" variant="outline" className="w-full rounded-full">Zrušit</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default EventsListAdmin;
